/**
 * useBatchManager — manages multiple concurrent SSE batch streams.
 *
 * Each addBatch() call opens a new SSE stream for that batchId.
 * Streams are managed imperatively via refs (not hooks) so the count can vary
 * dynamically without violating the rules of hooks.
 *
 * Supports polling fallback per-batch when SSE returns 503.
 *
 * Refresh restore: when BE ships an "active batches" endpoint, seed the manager
 * on mount by calling addBatch() for each active batchId with an empty files array.
 * The stream replays from offset 0, rebuilding per-file status automatically.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import auth from "@/services/auth";
import { streamBatchStatus, getBatchStatus, getActiveBatches, getAttachmentsByIds } from "../api/attachmentApi";
import {
  PROCESSING_STATUS,
  type BatchChildStatus,
  type BatchStatusResponse,
  type BatchFileSnapshot,
  type BatchEntry,
} from "../types/attachment";

const FAILED_STATUSES = new Set<string>([
  PROCESSING_STATUS.OCR_FAILED,
  PROCESSING_STATUS.VALIDATION_FAILED,
]);
const SUCCESS_STATUSES = new Set<string>([
  PROCESSING_STATUS.ALREADY_PROCESSED,
  PROCESSING_STATUS.VALIDATION_SUCCESS,
]);
// Statuses that are truly terminal for lifecycle transition gating.
// OCR_COMPLETED is intentionally excluded — it is an intermediate state that
// leads to validation. Including it would cause onLifecycleTransition to block
// VALIDATION_IN_PROGRESS and VALIDATION_SUCCESS updates.
const TERMINAL_LIFECYCLE_STATUSES = new Set<string>([
  PROCESSING_STATUS.VALIDATION_SUCCESS,
  PROCESSING_STATUS.VALIDATION_FAILED,
  PROCESSING_STATUS.OCR_FAILED,
  PROCESSING_STATUS.ALREADY_PROCESSED,
]);

function getToken(): string {
  try {
    return auth.getAccessToken();
  } catch {
    return "";
  }
}

function buildResponse(
  batchId: string,
  totalFiles: number,
  childMap: Map<string, BatchChildStatus>,
  isDone: boolean,
): BatchStatusResponse {
  const children = Array.from(childMap.values());
  const succeeded = children.filter((c) => SUCCESS_STATUSES.has(c.status)).length;
  const failed = children.filter((c) => FAILED_STATUSES.has(c.status)).length;
  const total = Math.max(totalFiles, children.length);
  return {
    batch_id: batchId,
    total,
    pending: Math.max(0, total - succeeded - failed),
    succeeded,
    failed,
    skipped: 0,
    done: isDone,
    children,
  };
}

export type UseBatchManagerReturn = {
  batches: BatchEntry[];
  /** Count of batches still in progress (not done). Used for the upload button badge. */
  activeBatchCount: number;
  addBatch: (batchId: string, files: BatchFileSnapshot[]) => void;
  dismissBatch: (batchId: string) => void;
};

export function useBatchManager(opts?: {
  /** Called whenever any batch transitions to done. Use to invalidate invoice list query. */
  onBatchComplete?: () => void;
}): UseBatchManagerReturn {
  const [batches, setBatches] = useState<BatchEntry[]>([]);

  // Per-batch stream AbortControllers: batchId → AbortController
  const streamsRef = useRef<Map<string, AbortController>>(new Map());
  // Per-batch child accumulation: batchId → (attachmentId → BatchChildStatus)
  const childMapsRef = useRef<Map<string, Map<string, BatchChildStatus>>>(new Map());
  // Per-batch total file count from batch_started SSE event
  const totalFilesRef = useRef<Map<string, number>>(new Map());
  // Per-batch polling intervals (active when SSE fell back to polling for that batch)
  const pollsRef = useRef<Map<string, ReturnType<typeof setInterval>>>(new Map());

  // Keep callback ref fresh without causing openStream to be recreated
  const onBatchCompleteRef = useRef(opts?.onBatchComplete);
  useEffect(() => {
    onBatchCompleteRef.current = opts?.onBatchComplete;
  });

  /** Recompute and push streamData for a single batch into React state. */
  const updateBatchData = useCallback((batchId: string, isDone: boolean) => {
    const childMap = childMapsRef.current.get(batchId);
    const total = totalFilesRef.current.get(batchId) ?? 0;
    if (!childMap) return;
    const response = buildResponse(batchId, total, childMap, isDone);
    setBatches((prev) =>
      prev.map((b) =>
        b.batchId === batchId
          ? { ...b, streamData: response, ...(isDone ? { done: true } : {}) }
          : b,
      ),
    );
  }, []);

  /** Abort stream + clear polling for a batch and fire onBatchComplete. */
  const finalizeBatch = useCallback((batchId: string) => {
    streamsRef.current.get(batchId)?.abort();
    streamsRef.current.delete(batchId);
    const poll = pollsRef.current.get(batchId);
    if (poll !== undefined) {
      clearInterval(poll);
      pollsRef.current.delete(batchId);
    }
    onBatchCompleteRef.current?.();
  }, []);

  /** Open an SSE stream for a given batchId and wire callbacks into shared state. */
  const openStream = useCallback(
    (batchId: string) => {
      const token = getToken();

      const ctrl = streamBatchStatus(batchId, token, {
        onTotalFiles(total) {
          totalFilesRef.current.set(batchId, total);
          updateBatchData(batchId, false);
        },

        onLifecycleTransition(payload) {
          const childMap = childMapsRef.current.get(batchId);
          if (!childMap) return;
          const existing = childMap.get(payload.attachment_id);
          const isTerminal =
            existing &&
            TERMINAL_LIFECYCLE_STATUSES.has(existing.status);
          if (!isTerminal) {
            childMap.set(payload.attachment_id, {
              attachment_id: payload.attachment_id,
              invoice_id: payload.invoice_id ?? existing?.invoice_id,
              status: payload.to_status,
              reason: existing?.reason,
            });
            updateBatchData(batchId, false);
          }
        },

        onChildResult(child) {
          const childMap = childMapsRef.current.get(batchId);
          if (!childMap) return;
          childMap.set(child.attachment_id, child);
          updateBatchData(batchId, false);
        },

        onComplete(successCount, failureCount) {
          const childMap = childMapsRef.current.get(batchId);
          if (!childMap) return;
          const countFromBE = successCount + failureCount;
          const finalTotal = Math.max(
            totalFilesRef.current.get(batchId) ?? 0,
            Number.isFinite(countFromBE) ? countFromBE : 0,
            childMap.size,
          );
          totalFilesRef.current.set(batchId, finalTotal);
          const response = buildResponse(batchId, finalTotal, childMap, true);
          setBatches((prev) =>
            prev.map((b) =>
              b.batchId === batchId ? { ...b, streamData: response, done: true } : b,
            ),
          );
          streamsRef.current.delete(batchId);
          onBatchCompleteRef.current?.();
        },

        onFallback() {
          setBatches((prev) =>
            prev.map((b) => (b.batchId === batchId ? { ...b, useFallback: true } : b)),
          );
          // Start per-batch polling interval
          let pollErrors = 0;
          const MAX_POLL_ERRORS = 3;
          const interval = setInterval(async () => {
            try {
              const status = await getBatchStatus(batchId);
              pollErrors = 0;
              setBatches((prev) =>
                prev.map((b) => (b.batchId === batchId ? { ...b, streamData: status } : b)),
              );
              if (status.done) {
                clearInterval(interval);
                pollsRef.current.delete(batchId);
                setBatches((prev) =>
                  prev.map((b) => (b.batchId === batchId ? { ...b, done: true } : b)),
                );
                onBatchCompleteRef.current?.();
              }
            } catch {
              pollErrors += 1;
              if (pollErrors < MAX_POLL_ERRORS) return; // transient — keep polling
              clearInterval(interval);
              pollsRef.current.delete(batchId);
              setBatches((prev) =>
                prev.map((b) => (b.batchId === batchId ? { ...b, done: true } : b)),
              );
              onBatchCompleteRef.current?.();
            }
          }, 3000);
          pollsRef.current.set(batchId, interval);
        },

        onFatalError(_status) {
          // 404/410 — batch expired; mark done with whatever data we have
          updateBatchData(batchId, true);
          finalizeBatch(batchId);
        },
      });

      streamsRef.current.set(batchId, ctrl);
    },
    [updateBatchData, finalizeBatch],
  );

  const addBatch = useCallback(
    (batchId: string, files: BatchFileSnapshot[]) => {
      // Initialise per-batch tracking refs BEFORE opening the stream so callbacks
      // that fire synchronously on the first event always find the maps initialised.
      childMapsRef.current.set(batchId, new Map());
      totalFilesRef.current.set(batchId, files.length);

      setBatches((prev) => [
        { batchId, files, streamData: null, done: false, useFallback: false },
        ...prev,
      ]);

      openStream(batchId);
    },
    [openStream],
  );

  const dismissBatch = useCallback((batchId: string) => {
    streamsRef.current.get(batchId)?.abort();
    streamsRef.current.delete(batchId);
    const poll = pollsRef.current.get(batchId);
    if (poll !== undefined) {
      clearInterval(poll);
      pollsRef.current.delete(batchId);
    }
    childMapsRef.current.delete(batchId);
    totalFilesRef.current.delete(batchId);
    setBatches((prev) => prev.filter((b) => b.batchId !== batchId));
  }, []);

  // On mount: fetch recent batches and restore them — both in-progress and completed.
  // Sorted newest-first to match addBatch prepend order.
  // Silently ignores errors — batch restore is a nice-to-have, not critical.
  useEffect(() => {
    let cancelled = false;

    getActiveBatches()
      .then(async ({ batches }) => {
        if (cancelled) return;
        const recentBatches = batches
          .sort((a, b) => new Date(b.started_at).getTime() - new Date(a.started_at).getTime());
        if (recentBatches.length === 0) return;

        // Fetch current batch status for each batch to get attachment IDs and per-file results.
        const statuses = await Promise.all(
          recentBatches.map((b) => getBatchStatus(b.batch_id).catch(() => null))
        );

        // Bulk-fetch attachment names so file names display instead of raw attachment IDs.
        const allIds = statuses.flatMap((s) => s?.children?.map((c) => c.attachment_id) ?? []);
        const nameMap: Record<string, string> = {};
        if (allIds.length > 0) {
          await getAttachmentsByIds(allIds)
            .then(({ attachments }) => {
              attachments.forEach((a) => { nameMap[a.id] = a.name; });
            })
            .catch(() => {}); // names are best-effort; fall back to attachment_id display
        }

        if (cancelled) return;

        // Build a Map keyed by batch_id so subsequent filter+map can look up status
        // by identity rather than by position in the filtered array.
        const statusByBatchId = new Map(
          recentBatches.map((b, i) => [b.batch_id, statuses[i]] as const)
        );

        recentBatches.forEach((b) => {
          if (b.done) return; // No refs needed for completed batches — no SSE stream opened
          // Skip if already tracked (e.g. a batch was just started before this fetch returned)
          if (streamsRef.current.has(b.batch_id)) return;
          childMapsRef.current.set(b.batch_id, new Map());
          totalFilesRef.current.set(b.batch_id, b.total);
        });

        setBatches((prev) => {
          const existingIds = new Set(prev.map((e) => e.batchId));
          const newEntries = recentBatches
            .filter((b) => !existingIds.has(b.batch_id))
            .map((b) => {
              const status = statusByBatchId.get(b.batch_id) ?? null;
              const files: BatchFileSnapshot[] = (status?.children ?? []).map((c) => ({
                attachmentId: c.attachment_id,
                name: nameMap[c.attachment_id] ?? c.attachment_id,
              }));
              return {
                batchId: b.batch_id,
                files,
                // Pre-populate counts from API so rows aren't blank while SSE catches up
                streamData: {
                  batch_id: b.batch_id,
                  total: b.total,
                  pending: b.pending,
                  succeeded: b.succeeded,
                  failed: b.failed,
                  skipped: b.skipped,
                  done: b.done,
                  children: status?.children ?? [],
                },
                done: b.done,
                useFallback: false,
              };
            });
          return newEntries.length > 0 ? [...newEntries, ...prev] : prev;
        });

        // Only open SSE streams for batches still in progress
        recentBatches
          .filter((b) => !b.done)
          .forEach((b) => {
            if (!streamsRef.current.has(b.batch_id)) {
              openStream(b.batch_id);
            }
          });
      })
      .catch(() => {
        // Silently ignore
      });

    return () => { cancelled = true; };
  }, [openStream]);

  // Abort all active streams and polls on unmount
  useEffect(() => {
    return () => {
      streamsRef.current.forEach((ctrl) => ctrl.abort());
      pollsRef.current.forEach((interval) => clearInterval(interval));
    };
  }, []);

  const activeBatchCount = batches.filter((b) => !b.done).length;

  return { batches, activeBatchCount, addBatch, dismissBatch };
}
